<template>
  <div class="keen-slider-wrapper">
    <div class="navigation-wrapper">
      <div ref="container" class="keen-slider">
        <slot />
      </div>
      <button
        v-if="arrows"
        :class="{
          'arrow--disabled': !loop && current === 0,
        }"
        class="btn-unstyled arrow arrow--left"
        @click="slider.prev()"
      >
        <svg height="26" width="14">
          <path d="M13 1 1 13l12 12" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
        </svg>
      </button>
      <button
        v-if="slider && arrows"
        :class="{
          'arrow--disabled': !loop && current === slider.track.details.slides.length - 1,
        }"
        class="arrow arrow--right btn-unstyled"
        @click="slider.next()"
      >
        <svg height="26" width="14">
          <path d="m1 25 12-12L1 1" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
        </svg>
      </button>
    </div>
    <div v-if="slider && dots" class="dots">
      <button
        v-for="index in dotHelper"
        :key="index"
        :class="{ active: current === index }"
        class="dot"
        @click="slider.moveToIdx(index)"
      />
    </div>
  </div>
</template>

<script setup>
import { useKeenSlider } from 'keen-slider/vue'
import { ref, computed } from 'vue'
import { bool, string, number } from 'vue-types'
import 'keen-slider/keen-slider.min.css'

const emit = defineEmits(['onSlideDataUpdated'])

// @sort-keys
const props = defineProps({
  animationDuration: number().def(5000),
  arrows: bool().def(true),
  autoSwitch: bool().def(false),
  autoSwitchTime: number().def(3000),
  autoplay: bool().def(false),
  dots: bool().def(true),
  drag: bool().def(false),
  dragSpeed: number().def(1),
  loop: bool().def(true),
  mode: string().def('snap'),
  performanceMode: bool().def(false),
  slideSpacingDesktop: number().def(15),
  slideSpacingMobile: number().def(5),
  slideSpacingSmallMobile: number().def(5),
  slideSpacingTablet: number().def(15),
  slideSpacingUltraWide: number().def(30),
  slidesOrigin: string().def('center'),
  slidesPerViewDesktop: number().def(1),
  slidesPerViewMobile: number().def(1),
  slidesPerViewSmallMobile: number().def(1),
  slidesPerViewTablet: number().def(1),
  slidesPerViewUltraWide: number().def(1),
})

const animation = { duration: props.animationDuration, easing: (t) => t }
const current = ref(0)

const getSlideDataset = (slider) => ({
  title: slider.slides[current.value]?.dataset?.title || '',
  artist: slider.slides[current.value]?.dataset?.artist || '',
})

// @sort-keys
const sliderOptions = {
  animationEnded: (s) => {
    if (props.autoplay) {
      s.moveToIdx(s.track.details.abs + 5, true, animation)
    }
  },
  breakpoints: {
    '(max-width: 350px)': {
      slides: {
        perView: props.slidesPerViewSmallMobile,
        spacing: props.slideSpacingSmallMobile,
        origin: props.slidesOrigin,
      },
    },
    '((min-width: 351px) and (max-width: 480px))': {
      slides: {
        perView: props.slidesPerViewMobile,
        spacing: props.slideSpacingMobile,
        origin: props.slidesOrigin,
      },
    },
    '((min-width: 481px) and (max-width: 768px))': {
      slides: {
        perView: props.slidesPerViewTablet,
        spacing: props.slideSpacingTablet,
        origin: props.slidesOrigin,
      },
    },
    '((min-width: 769px) and (max-width: 1200px))': {
      slides: {
        perView: props.slidesPerViewDesktop,
        spacing: props.slideSpacingDesktop,
        origin: props.slidesOrigin,
      },
    },
  },
  created: (s) => {
    emit('onSlideDataUpdated', getSlideDataset(s))

    if (props.autoplay) {
      s.moveToIdx(5, true, animation)
    }
  },
  drag: props.drag,
  dragSpeed: props.dragSpeed,
  initial: current.value,
  loop: props.loop,
  mode: props.mode,
  renderMode: props.performanceMode ? 'performance' : 'precision',
  slideChanged: (s) => {
    current.value = s.track.details.rel
    emit('onSlideDataUpdated', getSlideDataset(s))
  },
  slides: {
    perView: props.slidesPerViewUltraWide,
    spacing: props.slideSpacingUltraWide,
    origin: props.slidesOrigin,
  },
  updated: (s) => {
    if (props.autoplay) {
      s.moveToIdx(s.track.details.abs + 5, true, animation)
    }
  },
}

const sliderPlugins = [
  (slider) => {
    if (!props.autoSwitch) return

    let timeout
    let mouseOver = false
    function clearNextTimeout() {
      clearTimeout(timeout)
    }

    function nextTimeout() {
      clearTimeout(timeout)
      if (mouseOver) return
      timeout = setTimeout(() => {
        slider.next()
      }, props.autoSwitchTime)
    }

    slider.on('created', () => {
      slider.container.addEventListener('mouseover', () => {
        mouseOver = true
        clearNextTimeout()
      })
      slider.container.addEventListener('mouseout', () => {
        mouseOver = false
        nextTimeout()
      })
      nextTimeout()
    })
    slider.on('dragStarted', clearNextTimeout)
    slider.on('animationEnded', nextTimeout)
    slider.on('updated', nextTimeout)
  },
]

const [container, slider] = useKeenSlider(sliderOptions, sliderPlugins)
const dotHelper = computed(() =>
  slider.value ? [...Array.from({ length: slider.value.track.details.slides.length }).keys()] : []
)
</script>

<style lang="scss" scoped>
@import 'assets/scss/base/bootstrap_variables';
@import 'assets/scss/base/functions';

.keen-slider-wrapper {
  width: 100%;
  height: 100%;
}

.navigation-wrapper {
  position: relative;
}

.dots {
  display: flex;
  justify-content: center;
  padding: 6px 0;
  margin-top: -6px;
}

.dot {
  width: 10px;
  height: 10px;
  padding: 5px;
  margin: 0 12px;
  cursor: pointer;
  background: $color-white;
  border: 1px solid $color-dark;
  border-radius: 50%;
}

.dot:focus {
  outline: none;
}

.dot.active {
  background: $color-black;
}

.arrow {
  position: absolute;
  top: 50%;
  width: 30px;
  height: 30px;
  cursor: pointer;
  opacity: 1;
  transition: opacity $transition-duration;
  transform: translateY(-50%);

  @media (min-width: $screen-sm-min) {
    width: 20px;
    height: 20px;
  }

  svg {
    fill: none;
    stroke: $color-dark;
  }
}

.arrow--left {
  left: -10px;

  @media (min-width: $screen-sm-min) {
    left: 10px;
  }
}

.arrow--right {
  right: -10px;
  left: auto;

  @media (min-width: $screen-sm-min) {
    right: 10px;
  }
}

.arrow--disabled {
  pointer-events: none;
  opacity: 0;
}
</style>



// WEBPACK FOOTER //
// KeenSlider.vue